import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { CATS, useCatColors, fmtDur } from "../bits";
import { Sk } from "./storyBits";
import { ToolIcon } from "../../../components/classic/panels";

/* The accounting: where the hours actually went (flow bar + ranked list) and
   how steadily they ran (gauge + fragmentation heartbeat). All deterministic. */

export function StoryFlow({ apps = [], totals, loading, onOpenApp }) {
  const t = useDashTheme();
  const C = useCatColors();

  if (loading) {
    return (
      <div style={{ padding: t.s[5] }}>
        <Sk h={46} r={12} style={{ marginBottom: 16 }} />
        {[0, 1, 2, 3, 4].map((i) => <Sk key={i} h={38} style={{ marginBottom: 10 }} />)}
      </div>
    );
  }
  if (!apps.length) {
    return (
      <div style={{ padding: t.s[6], textAlign: "center", color: t.c.textTertiary, fontSize: t.font.size.base }}>
        No application time to break down for this day.
      </div>
    );
  }

  const total = Math.max(1, apps.reduce((s, a) => s + a.minutes, 0));
  const topMin = apps[0].minutes;

  return (
    <div style={{ padding: `${t.s[5]}px ${t.s[5]}px ${t.s[3]}px` }}>
      {/* The category donut that used to sit here grouped by `cat`, which is
          exactly what "What the hours went into" answers further up the page —
          two identical rings, one screen apart. This section is the ledger: one
          row per application, ranked, with its own mark. */}
      <div>
        {apps.map((a, i) => {
          const col = C[a.cat] || C.browse;
          const share = Math.round((a.minutes / total) * 100);
          return (
            <button
              key={a.name} type="button"
              className="st-reset st-approw st-focusable"
              onClick={() => onOpenApp?.(a)}
              style={{
                display: "flex", alignItems: "center", gap: 13, width: "100%",
                padding: "10px 6px", borderRadius: 10, cursor: "pointer",
                borderTop: i ? `1px solid ${t.c.border}` : "none",
              }}
            >
              {/* Was a single capital letter on a tinted square - "C" for both
                  Cursor and Chrome. ToolIcon draws the real brand mark, and
                  falls back to the kind-of-work glyph outside the registry. */}
              <ToolIcon name={a.name} cat={a.cat} size={34} />
              <span style={{ flex: "0 1 150px", minWidth: 90, textAlign: "left" }}>
                <span style={{ display: "block", fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>
                  {a.name}
                </span>
                <span style={{ display: "block", fontSize: 10.5, color: t.c.textTertiary, fontWeight: 600 }}>
                  {CATS[a.cat]?.label || a.cat}
                </span>
              </span>
              <span style={{ flex: 1, height: 7, borderRadius: 99, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover, overflow: "hidden" }}>
                <motion.i
                  initial={{ width: 0 }} animate={{ width: `${(a.minutes / topMin) * 100}%` }}
                  transition={{ duration: 0.8, ease: t.motion.ease.out, delay: Math.min(i * 0.05, 0.4) }}
                  style={{ display: "block", height: "100%", borderRadius: 99, background: col }}
                />
              </span>
              <span style={{ flex: "none", width: 56, textAlign: "right", fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, fontVariantNumeric: "tabular-nums" }}>
                {fmtDur(a.minutes)}
              </span>
              <span style={{ flex: "none", width: 34, textAlign: "right", fontSize: t.font.size.xs, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                {share}%
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Focus lab ─────────────────────────────────────────────────────────── */

export function StoryFocusLab({ totals, loading }) {
  const t = useDashTheme();

  if (loading || !totals) {
    return (
      <div style={{ padding: t.s[5] }}>
        <Sk w={120} h={64} style={{ marginBottom: 16 }} />
        <Sk h={56} />
      </div>
    );
  }

  const p = totals.focusPct || 0;
  const tone = p >= 65 ? t.status.positive : p >= 45 ? t.status.warning : t.status.negative;
  const R = 54, LEN = Math.PI * R;

  return (
    <div style={{ padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: t.s[4] }}>
        <span style={{ position: "relative", width: 120, height: 66, flex: "none" }}>
          <svg width="120" height="66" viewBox="0 0 120 66" aria-hidden="true">
            <path d="M6,60 A54,54 0 0 1 114,60" fill="none" strokeWidth="10" strokeLinecap="round"
              stroke={t.isDark ? t.c.surfaceMuted : t.c.surfaceHover} />
            <motion.path
              d="M6,60 A54,54 0 0 1 114,60" fill="none" strokeWidth="10" strokeLinecap="round"
              stroke={tone} strokeDasharray={LEN}
              initial={{ strokeDashoffset: LEN }} animate={{ strokeDashoffset: LEN - (LEN * p) / 100 }}
              transition={{ duration: 1.1, ease: t.motion.ease.out, delay: 0.2 }}
            />
          </svg>
          <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, textAlign: "center" }}>
            <span style={{ fontSize: 22, fontWeight: t.font.weight.semibold, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{p}%</span>
            <span style={{ display: "block", fontSize: 9.5, color: t.c.textTertiary, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em" }}>focus</span>
          </span>
        </span>
        <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.55 }}>
          <b style={{ color: t.c.text }}>{fmtDur(totals.longestBlockMin)}</b> longest unbroken run.<br />
          {fmtDur(totals.deepMin)} of {fmtDur(totals.trackedMin)} tracked time ran uninterrupted.
        </span>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: t.s[2] }}>
        {[
          { n: fmtDur(totals.longestBlockMin), l: "longest run" },
          { n: `${totals.switchesPerHour}/h`, l: "switch rate" },
          { n: fmtDur(totals.awayMin), l: "away time" },
        ].map((m) => (
          <span key={m.l} style={{
            flex: 1, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
            borderRadius: 12, padding: "11px 13px",
          }}>
            <span style={{ display: "block", fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{m.n}</span>
            <span style={{ display: "block", fontSize: 10.5, color: t.c.textTertiary, fontWeight: 600, marginTop: 2 }}>{m.l}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
